import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useWsNavigate } from "../hooks/useWsNavigate";
import { promptOptimizerAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/layout/PageHeader";
import { Loader2, CheckCircle, XCircle, RefreshCw } from "lucide-react";

const PromptOptimizationStatus = () => {
  const { id } = useParams();
  const navigate = useWsNavigate();
  const { currentWorkspace } = useAuth();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!currentWorkspace?.id || !id) return;

    const poll = async () => {
      try {
        const data = await promptOptimizerAPI.getOptimizationStatus(id, currentWorkspace.id);
        setStatus(data);

        if (data.status === "optimized") {
          clearInterval(intervalRef.current);
          // Navigate to diff page after a brief pause
          setTimeout(() => {
            navigate(`/prompt-optimization-diff-viewer/${id}`, {
              state: { fromStatus: true },
            });
          }, 1000);
        } else if (data.status === "failed") {
          clearInterval(intervalRef.current);
          setError(data.error_message || "Optimization failed");
        } else if (data.status === "merged" || data.status === "rejected") {
          clearInterval(intervalRef.current);
        }
      } catch (err) {
        console.error("Error polling status:", err);
      }
    };

    poll();
    intervalRef.current = setInterval(poll, 5000);

    return () => clearInterval(intervalRef.current);
  }, [id, currentWorkspace?.id, navigate, retryNonce]);

  const handleRetry = async () => {
    try {
      const opt = await promptOptimizerAPI.getOptimization(id, currentWorkspace.id);
      if (opt.diagnosis?.gaps?.length) {
        // GEPA-phase failure: re-run generate on the same record
        const selectedGapIds = opt.metadata?.selected_gap_ids?.length
          ? opt.metadata.selected_gap_ids
          : opt.diagnosis.gaps.map((g) => g.id);
        await promptOptimizerAPI.generate(id, selectedGapIds, currentWorkspace.id);
        setError(null);
        setStatus(null);
        setRetryNonce((n) => n + 1);
      } else {
        // Diagnosis-phase failure: no diagnosis yet, start fresh
        navigate("/prompt-optimizer/new");
      }
    } catch (err) {
      console.error("Error retrying:", err);
      alert("Failed to retry optimization");
    }
  };

  const isProcessing =
    status?.status === "optimizing" ||
    status?.status === "draft" ||
    status?.status === "diagnosing";

  return (
    <div className="px-4">
      <PageHeader
        title="Optimization Progress"
        description="Your prompt is being optimized."
        breadcrumbs={[
          { label: "Prompt Optimizer", path: "/prompt-optimizer" },
          { label: "Progress" },
        ]}
      />

      <div className="mt-12 flex justify-center">
        <div className="bg-card border border-border rounded-lg p-4 max-w-md w-full text-center">
          {isProcessing && (
            <>
              <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {status?.status === "diagnosing"
                  ? "Analyzing Calls…"
                  : "Optimizing Prompt..."}
              </h3>
              <p className="text-sm text-muted-foreground">
                {status?.status === "diagnosing"
                  ? "Reviewing your call transcripts before optimizing."
                  : "Generating the optimized instruction from the diagnosis. This usually takes a few seconds."}
              </p>
            </>
          )}

          {status?.status === "optimized" && (
            <>
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Optimization Complete
              </h3>
              <p className="text-sm text-muted-foreground mb-2">
                {status.summary}
              </p>
              <p className="text-xs text-muted-foreground">Redirecting to diff view...</p>
            </>
          )}

          {status?.status === "merged" && (
            <>
              <CheckCircle className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Optimization Merged
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                The optimized prompt has been applied to your agent.
              </p>
              <button
                onClick={() => navigate("/prompt-optimizer")}
                className="px-4 py-2 text-sm bg-muted text-foreground rounded-md hover:bg-accent"
              >
                Back to Optimizer
              </button>
            </>
          )}

          {status?.status === "rejected" && (
            <>
              <XCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Optimization Rejected
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                The proposed changes were not applied.
              </p>
              <button
                onClick={() => navigate("/prompt-optimizer")}
                className="px-4 py-2 text-sm bg-muted text-foreground rounded-md hover:bg-accent"
              >
                Back to Optimizer
              </button>
            </>
          )}

          {status?.status === "failed" && (
            <>
              <XCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Optimization Failed
              </h3>
              <p className="text-sm text-destructive mb-4">{error}</p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => navigate("/prompt-optimizer")}
                  className="px-4 py-2 text-sm bg-muted text-foreground rounded-md hover:bg-accent"
                >
                  Back
                </button>
                <button
                  onClick={handleRetry}
                  className="flex items-center space-x-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Retry</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromptOptimizationStatus;
